import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useAuth } from '../context/AuthContext';
import AppLogo from "./AppLogo";
import { tokens } from "../tokens";
import { useBreakpoint } from "../hooks/useBreakpoint";
import NotificationsDropdown from './NotificationsDropdown';
import { supabase } from '../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return "";
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60)   return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function avatarInitials(name = "") {
  const parts = name.trim().split(" ");
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "JN";
}

const AVATAR_COLORS = ["#16a34a","#0284c7","#7c3aed","#dc2626","#d97706","#0891b2","#db2777"];
function avatarBg(name = "") { return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length]; }

// ─── Location selector ────────────────────────────────────────────────────────
const CITIES = [
  "Nairobi", "Mombasa", "Kisumu", "Nakuru", "Eldoret", "Thika", "Nyeri",
  "Machakos", "Kakamega", "Kisii", "Meru", "Nyahururu", "Kericho", "Embu",
  "Naivasha", "Kitale", "Malindi", "Ruiru", "Kiambu", "Kajiado", "Voi",
  "Garissa", "Lamu", "Bungoma",
];

function LocationDropdown({ selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setDropPos({ top: rect.bottom + 6, left: rect.left });
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      const insideWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideWrapper && !insideDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (city) => { onSelect(city); setOpen(false); };

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed", top: dropPos.top + "px", left: dropPos.left + "px",
            width: 192, maxHeight: 320, overflowY: "auto",
            background: "#fff", borderRadius: 14,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #E5E7EB", padding: 8, zIndex: 99999,
            animation: "fadeSlideDown 0.15s ease both",
          }}
        >
          {CITIES.map((city) => (
            <div
              key={city}
              onClick={() => handleSelect(city)}
              className="text-sm px-3 py-2 cursor-pointer"
              style={{
                borderRadius: 8, transition: "all 0.15s ease",
                color: city === selected ? "#0d7a3c" : "#374151",
                fontWeight: city === selected ? 600 : 400,
                background: city === selected ? "#F0FBF5" : "transparent",
                fontFamily: "'Inter', system-ui, sans-serif",
              }}
              onMouseEnter={e => { if (city !== selected) e.currentTarget.style.background = "#F9FAFB"; }}
              onMouseLeave={e => { if (city !== selected) e.currentTarget.style.background = "transparent"; }}
            >
              {city}
            </div>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={wrapperRef} className="relative flex items-center px-3">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-gray-600 text-[13px] font-medium whitespace-nowrap hover:text-gray-900"
        style={{ fontFamily: "'Inter', system-ui, sans-serif", transition: "all 0.2s ease" }}
      >
        {selected}
        <span className="text-xs ml-0.5" style={{ fontSize: 10, opacity: 0.7 }}>▼</span>
      </button>
      {dropdown}
    </div>
  );
}

// ─── Messages Dropdown ────────────────────────────────────────────────────────
function MessagesDropdown({ unreadCount = 0, navigate }) {
  const [open, setOpen]       = useState(false);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref                   = useRef(null);
  const { user }              = useAuth();
  const displayCount          = unreadCount > 9 ? "9+" : unreadCount;
  const showBadge             = unreadCount > 0;

  useEffect(() => {
    const onMouse = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey   = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onMouse); document.removeEventListener("keydown", onKey); };
  }, []);

  const fetchThreads = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select(`
          id, updated_at,
          messages ( id, body, created_at, read_at, sender_id ),
          conversation_participants (
            user_id,
            profiles:user_id ( id, full_name, profile_photo )
          )
        `)
        .contains("participant_ids", [user.id])
        .order("updated_at", { ascending: false })
        .limit(5);

      if (error || !data) return;

      const mapped = data.map((conv) => {
        const msgs = (conv.messages || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const latest = msgs[0] || null;
        const other = (conv.conversation_participants || []).find(p => p.user_id !== user.id);
        const otherProfile = other?.profiles || null;
        const otherName = otherProfile?.full_name || "JiraniPro";
        const unread = msgs.filter(m => m.sender_id !== user.id && !m.read_at).length;
        return {
          id: conv.id, name: otherName,
          photo: otherProfile?.profile_photo || null,
          preview: latest?.body ? (latest.body.length > 52 ? latest.body.slice(0, 52) + "…" : latest.body) : "No messages yet",
          time: latest?.created_at || conv.updated_at,
          unread,
        };
      });
      setThreads(mapped);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { if (open) fetchThreads(); }, [open, fetchThreads]);

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Messages"
        style={{
          width: 34, height: 34, borderRadius: "50%",
          background: "#eef3fd", border: "1px solid #c7d7f7",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", position: "relative", padding: 0,
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(29,78,216,0.18)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
      >
        <MessageCircle size={15} color="#1d4ed8" strokeWidth={2} />
      </button>

      {showBadge && (
        <span style={{
          position: "absolute", top: -3, right: -3,
          minWidth: 15, height: 15,
          borderRadius: 999, background: "#b3261e", color: "#fff",
          fontSize: 9, fontWeight: 700, fontFamily: "'DM Sans', system-ui, sans-serif",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: displayCount === 1 ? "0" : "0 3px",
          border: "1.5px solid #fff", lineHeight: 1,
          pointerEvents: "none",
        }}>
          {displayCount}
        </span>
      )}

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 10px)", right: 0,
          width: 320, background: "#fff",
          border: "1px solid #F0F0F0", borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05)",
          zIndex: 9999, overflow: "hidden",
          fontFamily: "'Inter', system-ui, sans-serif",
        }}>
          <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: "#111827", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
              Messages {unreadCount > 0 && <span style={{ color: "#1e9254" }}>({unreadCount})</span>}
            </span>
          </div>

          <div style={{ maxHeight: 340, overflowY: "auto" }}>
            {loading ? (
              [1,2,3].map(i => (
                <div key={i} style={{ padding: "12px 16px", display: "flex", gap: 10, alignItems: "center", borderBottom: "1px solid #F9FAFB" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#f0f0f0", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: "45%", height: 11, borderRadius: 4, background: "#f0f0f0", marginBottom: 6 }} />
                    <div style={{ width: "80%", height: 10, borderRadius: 4, background: "#f5f5f5" }} />
                  </div>
                </div>
              ))
            ) : threads.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center" }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{ marginBottom: 10 }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, fontWeight: 500 }}>No conversations yet</p>
                <p style={{ fontSize: 12, color: "#d1d5db", margin: "4px 0 0" }}>Book a service to start chatting with a pro</p>
              </div>
            ) : (
              threads.map(thread => (
                <div
                  key={thread.id}
                  onClick={() => { setOpen(false); navigate(`/messages?thread=${thread.id}`); }}
                  style={{
                    padding: "11px 16px", display: "flex", gap: 10, alignItems: "center",
                    borderBottom: "1px solid #F9FAFB", cursor: "pointer",
                    background: thread.unread > 0 ? "#eef8f3" : "#fff",
                    borderLeft: `3px solid ${thread.unread > 0 ? "#1e9254" : "transparent"}`,
                    transition: "background 0.12s ease",
                  }}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                    background: thread.photo ? "transparent" : avatarBg(thread.name),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, color: "#fff", overflow: "hidden",
                    border: "1.5px solid #fff", boxShadow: "0 1px 3px rgba(13,46,26,0.05)",
                  }}>
                    {thread.photo
                      ? <img src={thread.photo} alt={thread.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : avatarInitials(thread.name)
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: thread.unread > 0 ? 700 : 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                        {thread.name}
                      </span>
                      <span style={{ fontSize: 10, color: "#9ca3af", flexShrink: 0, marginLeft: 6 }}>
                        {timeAgo(thread.time)}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12, color: thread.unread > 0 ? "#374151" : "#9ca3af", fontWeight: thread.unread > 0 ? 500 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {thread.preview}
                      </span>
                      {thread.unread > 0 && (
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1e9254", flexShrink: 0 }} />
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ padding: "12px 16px", borderTop: "1px solid #F3F4F6" }}>
            <button
              onClick={() => { setOpen(false); navigate("/messages"); }}
              style={{
                width: "100%", background: "#1e9254", color: "#fff", border: "none",
                borderRadius: 999, padding: "10px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", fontFamily: "'DM Sans', system-ui, sans-serif",
              }}
            >
              Open full inbox
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownRow({ label, iconPath, color, hoverBg = "#f7f8fa", onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: "100%", padding: "9px 16px", fontSize: 13,
        color: color || "#2d3549", fontWeight: color ? 600 : 400,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        cursor: "pointer", background: hov ? hoverBg : "transparent",
        border: "none", display: "flex", alignItems: "center", gap: 10,
        textAlign: "left", transition: "background 0.12s ease",
      }}
    >
      {iconPath && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color || "#8b93a7"} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d={iconPath}/>
        </svg>
      )}
      {label}
    </button>
  );
}

function ProfileDropdown({ user, onLogout, navigate, isProvider }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onMouse = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey   = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onMouse); document.removeEventListener("keydown", onKey); };
  }, []);

  const initials = user?.name ? user.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "JN";
  const photoUrl = user?.avatar_url || user?.profile_photo || user?.photo || null;

  const NAV_ITEMS = [
    { label: "Account",        path: isProvider ? "/pro/profile" : "/account", iconPath: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" },
    { label: "My Bookings",    path: "/bookings", iconPath: "M3 4h18v18H3zM16 2v6M8 2v6M3 10h18" },
    { label: "Help & Support", path: "/help", iconPath: "M12 22a10 10 0 100-20 10 10 0 000 20zM12 16v-2M12 8h.01" },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6, padding: 2,
          background: "none", border: "none", cursor: "pointer",
          borderRadius: 999, boxShadow: `0 0 0 2px ${open ? "#1e9254" : "#a7d9bc"}`,
          transition: "box-shadow 0.2s ease",
        }}
      >
        <div style={{ position: "relative" }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: photoUrl ? "transparent" : "linear-gradient(145deg, #1e925422, #eef8f3)",
            border: "2px solid #fff", boxShadow: "0 1px 4px rgba(30,146,84,0.18)",
            overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {photoUrl
              ? <img src={photoUrl} alt={user?.name || "Profile"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 12, fontWeight: 700, color: "#155c36", fontFamily: "'DM Sans', system-ui, sans-serif" }}>{initials}</span>}
          </div>
          <span style={{ position: "absolute", bottom: -1, right: -1, width: 9, height: 9, borderRadius: "50%", background: "#1e9254", border: "2px solid #fff" }}/>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b93a7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 10px)", zIndex: 9999,
          width: 252, background: "#fff", borderRadius: 16,
          border: "1.5px solid #e4e7ef", boxShadow: "0 10px 34px rgba(13,46,26,0.14)",
          overflow: "hidden",
        }}>
          <div style={{ padding: "14px 16px 12px", borderBottom: "1px solid #eef0f4" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f1117", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
              {user?.name || "Your account"}
            </div>
            <div style={{ fontSize: 11, color: "#8b93a7", fontFamily: "'DM Sans', system-ui, sans-serif", marginTop: 1 }}>
              {user?.phone || user?.email || ""}
            </div>
          </div>

          <div style={{ padding: "6px 0" }}>
            {NAV_ITEMS.map(item => (
              <DropdownRow key={item.label} label={item.label} iconPath={item.iconPath} onClick={() => { setOpen(false); navigate(item.path); }} />
            ))}
          </div>

          <div style={{ borderTop: "1px solid #eef0f4", padding: "6px 0" }}>
            {isProvider ? (
              <DropdownRow
                label="Switch to your Pro Account"
                iconPath="M16 3h5v5M4 20L21 3M8 3H3v5M3 16v5h5M21 21h-5v-5"
                onClick={() => { setOpen(false); navigate("/pro/dashboard"); }}
              />
            ) : (
              <DropdownRow
                label="Become a Pro"
                iconPath="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                color="#1e9254"
                onClick={() => { setOpen(false); navigate("/pro-onboarding"); }}
              />
            )}
          </div>

          <div style={{ borderTop: "1px solid #eef0f4", padding: "6px 0" }}>
            <DropdownRow
              label="Log Out"
              iconPath="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
              color="#b3261e"
              hoverBg="#fdf3f2"
              onClick={() => { setOpen(false); onLogout?.(); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Category & search data ───────────────────────────────────────────────────
const CATEGORIES = [
  { name: "Cleaning & Laundry", slug: "cleaning",
    columns: [{ heading: "Popular Services", links: ["Mama Fua (Laundry)", "Carpet & Upholstery", "Home Deep Clean", "Office Cleaning"] }] },
  { name: "Plumbing Services", slug: "plumbing",
    columns: [{ heading: "Popular Services", links: ["Pipe Repairs", "Drain Unblocking", "Tap Installation", "Water Tank Fixing"] }] },
  { name: "TV & Electronics Repair", slug: "electronics-repair",
    columns: [{ heading: "Popular Services", links: ["TV Screen Repair", "Home Theatre Setup", "Decoder Installation", "Appliance Repair"] }] },
  { name: "Hair & Beauty", slug: "hair-beauty",
    columns: [{ heading: "Popular Services", links: ["Hair Braiding", "Nails & Manicure", "Makeup Artist", "Lashes & Brows"] }] },
  { name: "Home Tutoring", slug: "home-tutoring",
    columns: [{ heading: "Popular Services", links: ["Primary Tutoring", "High School Subjects", "University Help", "KCSE Prep"] }] },
  { name: "Photography", slug: "photography",
    columns: [{ heading: "Popular Services", links: ["Event Photography", "Portrait Sessions", "Product Shoots", "Videography"] }] },
  { name: "Electrical Services", slug: "electrical",
    columns: [{ heading: "Popular Services", links: ["Wiring & Rewiring", "Solar Installation", "Lighting Setup", "Generator Repair"] }] },
  { name: "Gardening & Landscaping", slug: "gardening",
    columns: [{ heading: "Popular Services", links: ["Lawn Mowing", "Garden Design", "Tree Trimming", "Irrigation Setup"] }] },
  { name: "Carpentry & Furniture", slug: "carpentry-furniture",
    columns: [{ heading: "Popular Services", links: ["Custom Furniture", "Cabinet Installation", "Furniture Repair", "Wood Finishing"] }] },
  { name: "Moving & Relocation", slug: "moving",
    columns: [{ heading: "Popular Services", links: ["House Moving", "Office Relocation", "Packing Services", "Furniture Transport"] }] },
  { name: "Babysitting & Childcare", slug: "childcare",
    columns: [{ heading: "Popular Services", links: ["Babysitting", "Nanny Services", "After-School Care", "Newborn Care"] }] },
  { name: "Fitness & Training", slug: "fitness",
    columns: [{ heading: "Popular Services", links: ["Personal Training", "Home Workouts", "Yoga Sessions", "Nutrition Coaching"] }] },
  { name: "Auto Repair", slug: "auto-repair",
    columns: [{ heading: "Popular Services", links: ["Engine Diagnostics", "Brake Repair", "Oil Change", "Car Servicing"] }] },
  { name: "Errands & Delivery", slug: "errands-delivery",
    columns: [{ heading: "Popular Services", links: ["Grocery Shopping", "Package Delivery", "Pickup & Drop-off", "Queue Standing"] }] },
  { name: "Personal Chef", slug: "chef",
    columns: [{ heading: "Popular Services", links: ["Private Chef", "Catering Services", "Meal Prep", "Cooking Classes"] }] },
];

const SEARCH_SUGGESTIONS = [
  "Cleaning & Laundry", "Carpet Cleaning", "Deep House Cleaning",
  "Office Cleaning", "Laundry & Dry Cleaning", "Airbnb Cleaning",
  "Plumbing Services", "Pipe Repair", "Drain Unblocking",
  "Water Heater Installation", "Borehole Services",
  "Electrical Services", "Electrical Installation", "Solar Panel Setup", "Generator Repair",
  "Electrical Wiring", "AC Installation & Repair",
  "TV & Electronics Repair", "TV Repair", "Appliance Repair", "Phone Repair", "AC Repair",
  "Hair & Beauty", "Hair Braiding", "Hair Coloring", "Nails & Manicure",
  "Makeup Artist", "Barber Services",
  "Boda Boda Rides", "Boda Boda Delivery", "Boda Boda Transport",
  "Courier Delivery", "Moving & Relocation", "Furniture Delivery",
  "Photography", "Event Photography", "Portrait Photography", "Videography",
  "Home Tutoring", "KCSE Exam Preparation", "Mathematics Tutoring",
  "Babysitting & Childcare", "Babysitting", "Nanny Services", "After-School Care",
  "Fitness & Training", "Personal Trainer", "Home Workouts", "Yoga Instructor",
  "Nutrition Coaching", "Massage Therapy",
  "Private Chef", "Catering Services", "Meal Prep",
  "Carpentry & Furniture", "Carpentry", "Custom Furniture", "Cabinet Installation",
  "Furniture Assembly", "Furniture Repair", "Interior Painting",
  "Gardening & Landscaping", "Lawn Mowing", "Dog Grooming",
  "Auto Repair", "Engine Diagnostics", "Brake Repair", "Car Servicing", "Oil Change",
  "Errands & Delivery", "Grocery Shopping", "Package Delivery", "Queue Standing",
];

const PROVIDER_NAV = [
  { label: "Jobs",        path: "/jobs"            },
  { label: "My Services", path: "/manage-services" },
];

const CUSTOMER_LOGGED_IN_NAV = [
  { label: "Find Help",           path: "/search"       },
  { label: "How JiraniApp Works", path: "/how-it-works" },
  { label: "JiraniApp Guarantee", path: "/guarantee"    },
  { label: "Refer & Earn",        path: "/refer"        },
  { label: "Post a Job",          path: "/post-job"     },
];

// ─── Main Navbar ──────────────────────────────────────────────────────────────
export default function Navbar({ navQuery, setNavQuery, showNavSearch, openAuth }) {
  const [openPro, setOpenPro]                       = useState(false);
  const [openBrowse, setOpenBrowse]                 = useState(false);
  const [scrolled, setScrolled]                     = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen]         = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen]   = useState(false);

  const navRef       = useRef(null);
  const navigate     = useNavigate();
  const { isLoggedIn, logoutUser, user } = useAuth();
  const isProvider = user?.role === "provider";
  const { isMobile, isTablet } = useBreakpoint();

  const [selectedLocation, setSelectedLocation] = useState(() => {
    try { return localStorage.getItem("jirani_location") || "Nairobi"; } catch { return "Nairobi"; }
  });

  const handleLocationChange = (city) => {
    setSelectedLocation(city);
    try { localStorage.setItem("jirani_location", city); } catch {}
    window.dispatchEvent(new CustomEvent("jirani:locationchange", { detail: city }));
    if (typeof window !== "undefined" && window.location.pathname === "/search") {
      const params = new URLSearchParams(window.location.search);
      params.set("location", city);
      navigate(`/search?${params.toString()}`, { replace: true });
    }
  };

  useEffect(() => {
    const handler = (e) => setSelectedLocation(e.detail);
    window.addEventListener("jirani:locationchange", handler);
    return () => window.removeEventListener("jirani:locationchange", handler);
  }, []);

  const buildSearchUrl = (extraParams = {}) => {
    const params = new URLSearchParams(extraParams);
    if (selectedLocation) params.set("location", selectedLocation);
    const qs = params.toString();
    return qs ? `/search?${qs}` : "/search";
  };

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) { setOpenPro(false); setOpenBrowse(false); }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => { if (!isMobile) setMobileMenuOpen(false); }, [isMobile]);

  const doSearch = (q) => { if (!q.trim()) return; navigate(buildSearchUrl({ q: q.trim() })); };
  const handleNavClick = (path) => { setMobileMenuOpen(false); setOpenPro(false); setOpenBrowse(false); navigate(path); };
  const handleCategoryClick = (slug) => { setMobileMenuOpen(false); navigate(buildSearchUrl({ category: slug })); };

  const navHeight   = isMobile ? 60 : 72;
  const padX        = isMobile ? tokens.responsive.mobile.pagePaddingX : isTablet ? tokens.responsive.tablet.pagePaddingX : tokens.spacing[24];
  const btnFontSize = isMobile ? tokens.font.size.sm : tokens.font.size.md;

  const mobileLoggedInLinks = isProvider ? PROVIDER_NAV : [...CUSTOMER_LOGGED_IN_NAV];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');
        html, body { overflow-x: hidden; max-width: 100vw; }
        .nav-link { background: none; border: none; cursor: pointer; font-family: 'DM Sans', system-ui, sans-serif; font-size: 13.5px; font-weight: 500; color: #4a5268; padding: 0; white-space: nowrap; min-height: ${tokens.spacing[44]}px; display: flex; align-items: center; letter-spacing: -0.1px; transition: color 0.18s ease, font-weight 0.18s ease; }
        .nav-link:hover { color: #0f1117; font-weight: 600; }
        .nav-link.active-link { color: #1e9254; font-weight: 600; }
        .dropdown-item { padding: ${tokens.spacing[10]}px ${tokens.spacing[12]}px; border-radius: ${tokens.radius.md}px; cursor: pointer; font-size: ${tokens.font.size.md}px; color: ${tokens.color.neutral700}; font-family: 'Inter', system-ui, sans-serif; min-height: ${tokens.spacing[44]}px; display: flex; align-items: center; letter-spacing: 0.2px; transition: background 0.2s ease; }
        .dropdown-item:hover { background: ${tokens.color.neutral100}; }
        .cta-nav-btn:hover { transform: translateY(-1px); background: #008a45 !important; box-shadow: 0 6px 18px rgba(0,0,0,0.13) !important; }
      `}</style>

      <header ref={navRef} style={{
        fontFamily: "'Inter', system-ui, sans-serif",
        position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000,
        background: tokens.color.white,
        boxShadow: scrolled ? "0 2px 12px rgba(0,0,0,0.07)" : "none",
        transition: `box-shadow ${tokens.motion.medium} ${tokens.motion.ease}`,
      }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: `0 ${padX}px`, height: navHeight, display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacing[16], overflow: "visible", minWidth: 0 }}>

          <AppLogo size={isMobile ? "sm" : "md"} />

          {showNavSearch && !isMobile && (
            <div style={{ flex: 1, maxWidth: isTablet ? 320 : 480, margin: `0 ${isTablet ? tokens.spacing[16] : tokens.spacing[32]}px`, position: "relative" }} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", background: tokens.color.white, border: `1.5px solid ${tokens.color.neutral300}`, borderRadius: 999, padding: `${tokens.spacing[6]}px ${tokens.spacing[6]}px ${tokens.spacing[6]}px ${tokens.spacing[16]}px`, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", transition: "box-shadow 0.2s ease" }}>
                <input
                  value={navQuery || ""}
                  onChange={e => setNavQuery && setNavQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") doSearch(navQuery); }}
                  placeholder="What service are you looking for?"
                  style={{ flex: 1, height: tokens.spacing[32], border: "none", outline: "none", fontSize: tokens.font.size.md, fontFamily: "'Inter', system-ui, sans-serif", color: tokens.color.neutral900, background: "transparent", letterSpacing: "0.2px" }}
                />
                <div style={{ width: 1, height: 20, background: tokens.color.neutral300, margin: "0 6px", flexShrink: 0 }} />
                <LocationDropdown selected={selectedLocation} onSelect={handleLocationChange} />
                <button onClick={() => doSearch(navQuery)} style={{ background: "#00994d", border: "none", borderRadius: "50%", width: tokens.spacing[36], height: tokens.spacing[36], display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, minHeight: tokens.spacing[44], transition: "all 0.2s ease", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#008a45"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#00994d"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                </button>
              </div>
            </div>
          )}

          {/* ── Desktop right side ── */}
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: `clamp(${tokens.spacing[16]}px, 2vw, ${tokens.spacing[32]}px)`, fontSize: `clamp(13px, 1.2vw, 15px)`, fontWeight: 550, color: "#1F2937", flexShrink: 0, whiteSpace: "nowrap", marginLeft: "auto", fontFamily: "'Inter', system-ui, sans-serif", letterSpacing: "0.2px" }}>

              {!isLoggedIn && (
                <>
                  {/* "How it Works" → footer info page */}
                  <button className="nav-link" onClick={() => handleNavClick("/how-it-works")}>How it Works</button>
                  {/* "For Customers" → same info page, customer-framed anchor if present */}
                  <button className="nav-link" onClick={() => handleNavClick("/how-it-works")}>For Customers</button>
                  {/* "For JiraniPros" → become-a-pro page, styled as the active/highlighted nav item */}
                  <button className="nav-link active-link" onClick={() => handleNavClick("/become-a-pro")}>For JiraniPros</button>
                  {/* "Reviews" → reviews page or homepage anchor */}
                  <button className="nav-link" onClick={() => handleNavClick("/reviews")}>Reviews</button>
                  {/* "Find Help" → replaces old browse-categories dropdown + verified-pros badge; goes straight to search */}
                  <button className="nav-link" onClick={() => navigate(buildSearchUrl())}>Find Help</button>
                  {/* "Log in" → login modal */}
                  <button className="nav-link" onClick={() => openAuth("login")}>Log in</button>
                  {/* "Earn as a JiraniPro" → pro signup page (not login) */}
                  <button className="cta-nav-btn" onClick={() => handleNavClick("/become-a-pro")} style={{ background: "#00994d", color: tokens.color.white, border: "none", borderRadius: 13, padding: `${tokens.spacing[8]}px ${tokens.spacing[20]}px`, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: btnFontSize, flexShrink: 0, minHeight: tokens.spacing[44], letterSpacing: "0.2px", boxShadow: "0 2px 10px rgba(0,0,0,0.10)", transition: "all 0.2s ease" }}>
                    Earn as a JiraniPro
                  </button>
                </>
              )}

              {isLoggedIn && (
                <>
                  {(isProvider ? PROVIDER_NAV : CUSTOMER_LOGGED_IN_NAV).map(item => (
                    <button key={item.path} className="nav-link" onClick={() => handleNavClick(item.path)}>
                      {item.label}
                    </button>
                  ))}

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
                    <MessagesDropdown unreadCount={user?.unreadMessages ?? 0} navigate={navigate} />
                    <NotificationsDropdown />
                    <ProfileDropdown user={user} onLogout={logoutUser} navigate={navigate} isProvider={isProvider} />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <button onClick={() => setMobileMenuOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", padding: tokens.spacing[8], marginLeft: "auto", minHeight: tokens.spacing[44], minWidth: tokens.spacing[44], display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={tokens.color.neutral700} strokeWidth="2" strokeLinecap="round">
                {mobileMenuOpen ? <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></> : <><path d="M3 12h18"/><path d="M3 6h18"/><path d="M3 18h18"/></>}
              </svg>
            </button>
          )}
        </div>

        {/* ── MOBILE DRAWER ── */}
        {isMobile && mobileMenuOpen && (
          <div style={{ background: tokens.color.white, borderTop: `1px solid ${tokens.color.neutral200}`, padding: `${tokens.spacing[16]}px ${tokens.spacing[16]}px ${tokens.spacing[24]}px`, display: "flex", flexDirection: "column", gap: tokens.spacing[4] }}>
            {showNavSearch && (
              <div style={{ display: "flex", alignItems: "center", background: tokens.color.neutral100, borderRadius: tokens.radius.md, padding: `${tokens.spacing[8]}px ${tokens.spacing[12]}px`, marginBottom: tokens.spacing[8], gap: tokens.spacing[8] }}>
                <input
                  value={navQuery || ""}
                  onChange={e => setNavQuery && setNavQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { doSearch(navQuery); setMobileMenuOpen(false); } }}
                  placeholder="Search services..."
                  style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: tokens.font.size.md, fontFamily: "'Inter', system-ui, sans-serif", color: tokens.color.neutral900, minHeight: tokens.spacing[36], letterSpacing: "0.2px" }}
                />
                <button onClick={() => { doSearch(navQuery); setMobileMenuOpen(false); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tokens.color.neutral500} strokeWidth="1.75" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                </button>
              </div>
            )}

            {!isLoggedIn ? (
              <>
                {[
                  { label: "Find Services",      action: () => handleNavClick("/search")       },
                  { label: "Hire a JiraniPro",   action: () => handleNavClick("/search")       },
                  { label: "Become a JiraniPro", action: () => handleNavClick("/become-a-pro") },
                  { label: "How it works",       action: () => handleNavClick("/how-it-works") },
                ].map((item, i) => (
                  <button key={i} onClick={item.action} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: `${tokens.spacing[12]}px ${tokens.spacing[8]}px`, fontSize: tokens.font.size.md, fontWeight: 550, color: tokens.color.neutral700, fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, borderBottom: `1px solid ${tokens.color.neutral100}`, letterSpacing: "0.2px" }}>
                    {item.label}
                  </button>
                ))}
                <div style={{ marginTop: tokens.spacing[8], display: "flex", flexDirection: "column", gap: tokens.spacing[8] }}>
                  <button onClick={() => { setMobileMenuOpen(false); openAuth("login"); }} style={{ background: tokens.color.white, border: `1.5px solid ${tokens.color.neutral200}`, borderRadius: 13, padding: `${tokens.spacing[12]}px`, fontSize: tokens.font.size.md, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, color: tokens.color.neutral700, letterSpacing: "0.2px" }}>Log in</button>
                  {/* Mobile CTA → /become-a-pro page, not openAuth("pro") */}
                  <button onClick={() => { setMobileMenuOpen(false); navigate("/become-a-pro"); }} style={{ background: "#00994d", color: tokens.color.white, border: "none", borderRadius: 13, padding: `${tokens.spacing[12]}px`, fontSize: tokens.font.size.md, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, letterSpacing: "0.2px", boxShadow: "0 2px 10px rgba(0,0,0,0.10)" }}>Earn as a JiraniPro</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ borderBottom: `1px solid ${tokens.color.neutral100}` }}>
                  <button
                    onClick={() => setMobileExploreOpen(o => !o)}
                    style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: `${tokens.spacing[12]}px ${tokens.spacing[8]}px`, fontSize: tokens.font.size.md, fontWeight: 550, color: tokens.color.neutral700, fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, letterSpacing: "0.2px", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <span>Explore</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" style={{ transform: mobileExploreOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease", flexShrink: 0 }}>
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  {mobileExploreOpen && (
                    <div style={{ paddingBottom: tokens.spacing[8], maxHeight: 320, overflowY: "auto" }}>
                      {CATEGORIES.map((cat) => (
                        <button
                          key={cat.name}
                          onClick={() => { setMobileMenuOpen(false); navigate(buildSearchUrl({ category: cat.slug })); }}
                          style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: `${tokens.spacing[10]}px ${tokens.spacing[8]}px ${tokens.spacing[10]}px ${tokens.spacing[20]}px`, fontSize: tokens.font.size.base, fontWeight: 450, color: tokens.color.neutral400, fontFamily: "'Inter', system-ui, sans-serif", width: "100%", display: "block", letterSpacing: "0.2px" }}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {mobileLoggedInLinks.map((item, i) => (
                  <button key={i} onClick={() => handleNavClick(item.path)} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: `${tokens.spacing[12]}px ${tokens.spacing[8]}px`, fontSize: tokens.font.size.md, fontWeight: 550, color: tokens.color.neutral700, fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, borderBottom: `1px solid ${tokens.color.neutral100}`, letterSpacing: "0.2px", width: "100%" }}>
                    {item.label}
                  </button>
                ))}

                <button onClick={() => handleNavClick("/messages")} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: `${tokens.spacing[12]}px ${tokens.spacing[8]}px`, fontSize: tokens.font.size.md, fontWeight: 550, color: tokens.color.neutral700, fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, borderBottom: `1px solid ${tokens.color.neutral100}`, letterSpacing: "0.2px", width: "100%" }}>
                  Messages
                </button>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${tokens.spacing[12]}px ${tokens.spacing[8]}px`, borderBottom: `1px solid ${tokens.color.neutral100}`, minHeight: 48 }}>
                  <span style={{ fontSize: tokens.font.size.md, fontWeight: 550, color: tokens.color.neutral700, fontFamily: "'Inter', system-ui, sans-serif", letterSpacing: "0.2px" }}>
                    Notifications
                  </span>
                  <NotificationsDropdown />
                </div>

                {(isProvider
                  ? [{ label: "Dashboard", path: "/pro/dashboard" }, { label: "My Profile", path: "/pro/profile" }]
                  : [{ label: "Account Settings", path: "/account" }]
                ).map((item) => (
                  <button key={item.path} onClick={() => handleNavClick(item.path)} style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: `${tokens.spacing[12]}px ${tokens.spacing[8]}px`, fontSize: tokens.font.size.md, fontWeight: 550, color: tokens.color.neutral700, fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, borderBottom: `1px solid ${tokens.color.neutral100}`, letterSpacing: "0.2px", width: "100%" }}>
                    {item.label}
                  </button>
                ))}

                <button
                  onClick={() => { setMobileMenuOpen(false); navigate(isProvider ? "/account" : "/pro-onboarding"); }}
                  style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: `${tokens.spacing[12]}px ${tokens.spacing[8]}px`, fontSize: tokens.font.size.md, fontWeight: 550, color: isProvider ? tokens.color.neutral700 : "#1e9254", fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, borderBottom: `1px solid ${tokens.color.neutral100}`, letterSpacing: "0.2px", width: "100%" }}
                >
                  {isProvider ? "Switch to Customer Mode" : "Become a Pro"}
                </button>
                <button onClick={() => { setMobileMenuOpen(false); logoutUser(); }} style={{ background: tokens.color.neutral100, border: "none", borderRadius: 13, padding: `${tokens.spacing[12]}px`, fontSize: tokens.font.size.md, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter', system-ui, sans-serif", minHeight: 48, color: tokens.color.neutral700, letterSpacing: "0.2px", marginTop: tokens.spacing[8] }}>Log out</button>
              </>
            )}
          </div>
        )}
      </header>
    </>
  );
}